import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Url } from '@wix/da-url';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  BiContext,
  BiLoggerContextProvider,
} from '@wix/da-bi/pkg/BiLogger.context';
import { OverlayContextProvider } from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Standard/Overlay/OverlayContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useFirstTimeFlag } from '@wix/da-shared-react/pkg/redux/firstTimeFlags/hooks';
import { FirstTimeFlag } from '@wix/da-shared-react/pkg/redux/firstTimeFlags/constants';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import type { BIEventPayload } from '@wix/da-bi/pkg/types';
import type { BrowseItem } from '../../../../../types/api';
import { isDeviation } from '../../../../helpers/streams';
import DeviantsBar from '../../../DeviantsBar';
import BasePageLayout from '../../_partials/BasePageLayout';
import DefaultPageContent from '../../_partials/DefaultPageContent';
import ViewSwitcher from '../../_partials/ViewSwitcher';
import DeviantsSearchButton from '../../../DeviantsSearchButton';
import DesktopWatchFTUE from './DesktopWatchFTUE';

import s from './WatchPage.scss';

export interface Props {
  feedType?: string;
  isStacked?: boolean;
  isEmpty?: boolean;
  watchCount: number;
  removeDeviation: (deviation: PapiDeviation, offset: number) => void;
  removeAllFromStack: (
    biData: BIEventPayload,
    stack?: {
      deviation: PapiDeviation;
      offset: number;
    }
  ) => void;
}

const WatchPage: React.FC<Props> = ({
  feedType,
  isStacked,
  watchCount,
  isEmpty,
  removeAllFromStack,
  removeDeviation,
}) => {
  const { t } = useTranslation();
  const biData = useContext(BiContext);
  const isMobile = useContext(MobileContext);
  const isDyw3 = useFlag('dyw3');
  const { hasFlag: showFTUE } = useFirstTimeFlag(
    FirstTimeFlag.IsDyw3RemoveNotSeen
  );

  const getItemUrl = useCallback(
    (item: BrowseItem) => {
      if (!isDeviation(item) || !item.author) {
        return;
      }

      return Url.browseDeviantsYouWatchLink(
        feedType as any,
        item.author.username
      );
    },
    [feedType]
  );

  const onRemove = useMemo(() => {
    if (!isDyw3) {
      return;
    }

    if (!isStacked) {
      return removeDeviation;
    }

    return (deviation: PapiDeviation, offset: number) =>
      removeAllFromStack(
        { ...biData, is_grouped: isStacked ? 1 : 0, sectionname: 'general' },
        { deviation, offset }
      );
  }, [biData, isDyw3, isStacked, removeAllFromStack, removeDeviation]);

  const showViewSwitcher = feedType === 'deviations' && !isEmpty;

  return (
    <BiLoggerContextProvider
      value={{
        is_grouped: isStacked ? 1 : 0,
      }}
    >
      <BasePageLayout
        title={isMobile ? null : t('subnavigation.title.watch')}
        tagLine={
          isMobile || isEmpty
            ? undefined
            : t('subnavigation.subtitle.watchingDeviants', {
                count: watchCount,
              })
        }
        customHeader={!isEmpty && <DeviantsBar />}
        withNavigation={!isEmpty}
        headerIsStickyOnScrollUp={!isEmpty}
        headerLeftSideContent={
          isMobile && showViewSwitcher && <ViewSwitcher isStacked={isStacked} />
        }
        headerRightSideContent={
          isMobile ? (
            <DeviantsSearchButton />
          ) : (
            showViewSwitcher && (
              <div className={s['view-switcher']}>
                <ViewSwitcher isStacked={isStacked} />
              </div>
            )
          )
        }
        contentClassName={classnames(
          s['content'],
          showViewSwitcher && s['with-view-switcher']
        )}
      >
        <OverlayContextProvider
          value={{
            showPermanentEngagementIcon: true,
          }}
        >
          {isDyw3 && showFTUE && showViewSwitcher && !isMobile && (
            <DesktopWatchFTUE className={s['ftue']} />
          )}
          <DefaultPageContent
            getItemUrl={isStacked ? getItemUrl : undefined}
            onRemove={onRemove}
            contentStripIndex={3}
          />
        </OverlayContextProvider>
      </BasePageLayout>
    </BiLoggerContextProvider>
  );
};
WatchPage.displayName = 'WatchPage';

export default WatchPage;
